import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  DefaultAuthService,
  LOGIN_CONFIG,
  LoginConfig,
  UsuarioDTO,
  Verify2FaRequestDto,
  ResultadoAuth,
  Result,
  ILoginAlertService,
  ILoginModalService
} from 'Login_Angular9_2.0';
import Swal from 'sweetalert2';
import { LocalStorageService } from '../localstorage/localstorage.service';
import { UsuariosService } from '../oficialia-partes/usuarios.service';
import { VariablesEnum } from '../../enum/variables.enum';
import { HeadersEnum } from '../../enum/headers.enum';
import { OFP_USUARIO } from '../../models/nomina-conade.model';

@Injectable({
  providedIn: 'root'
})
export class BovedaAuthService extends DefaultAuthService {

  constructor(
    http: HttpClient,
    @Inject(LOGIN_CONFIG) config: LoginConfig,
    private localStorageService: LocalStorageService,
    private usuariosService: UsuariosService
  ) {
    super(http, config);
  }

  getUserInSession(): any {
    const session = this.localStorageService.getJsonValue(VariablesEnum.usuario_conade);
    if (!session) {
      return null;
    }
    try {
      return typeof session === 'string' ? JSON.parse(session) : session;
    } catch {
      return session;
    }
  }

  async login(credentials: UsuarioDTO): Promise<ResultadoAuth | undefined> {
    const result = await super.login(credentials);
    if (result && result.success && result.code !== 202 && !(result.data && result.data.requiresTwoFactor)) {
      return await this.vincularUsuarioSac(result);
    }
    return result;
  }

  async verify2Fa(verifyDto: Verify2FaRequestDto): Promise<ResultadoAuth | undefined> {
    const result = await super.verify2Fa(verifyDto);
    if (result && result.success) {
      return await this.vincularUsuarioSac(result);
    }
    return result;
  }

  private async vincularUsuarioSac(result: ResultadoAuth): Promise<ResultadoAuth> {
    try {
      if (result.data && result.data.accessToken) {
        this.loginUser(result.data.accessToken, result.data.refreshToken || '');
      }
      const idUsuarioBoveda = result.data && result.data.catUsuarioId;
      const usuarioExiste = await this.usuariosService.consultar('?bovedA_USUARIO_ID=' + Number(idUsuarioBoveda));

      if (usuarioExiste && usuarioExiste.status === 1 && usuarioExiste.response && usuarioExiste.response.length > 0) {
        const usuario: OFP_USUARIO = usuarioExiste.response[0];
        this.localStorageService.setJsonValue(VariablesEnum.usuario_conade, usuario);
        localStorage.setItem(HeadersEnum.USUARIO, usuario.uS_CUENTA || '');
        localStorage.setItem(HeadersEnum.ACCION, '');
        return result;
      }

      return {
        success: false,
        code: 403,
        message: 'El usuario no tiene acceso al sistema, comuníquese con el área de soporte técnico por medio de mesa de servicio https://mesadeservicio.conade.gob.mx/'
      };
    } catch (error) {
      return {
        success: false,
        code: 500,
        message: 'No fue posible validar el usuario en el servicio de Oficialía de Partes.'
      };
    }
  }
}

const fireAlert = (options: any) => {
  const fn = (Swal as any).fire ? (Swal as any).fire.bind(Swal) : (Swal as any);
  return fn(options);
};

@Injectable({
  providedIn: 'root'
})
export class BovedaAlertService implements ILoginAlertService {
  showError(message: string): void {
    fireAlert({
      title: 'Error',
      text: message,
      type: 'error',
      confirmButtonColor: '#3085d6',
      confirmButtonText: 'Aceptar'
    });
  }

  showSuccess(message: string): void {
    fireAlert({
      title: 'Éxito',
      text: message,
      type: 'success',
      confirmButtonColor: '#3085d6',
      confirmButtonText: 'Aceptar'
    });
  }

  showInfo(message: string): void {
    fireAlert({
      title: 'Información',
      text: message,
      type: 'info',
      confirmButtonColor: '#3085d6',
      confirmButtonText: 'Aceptar'
    });
  }
}

@Injectable({
  providedIn: 'root'
})
export class BovedaModalService implements ILoginModalService {
  openRightModal(message: string, title?: string): void {
    fireAlert({
      title: title || 'Aviso',
      text: message,
      type: 'warning',
      confirmButtonColor: '#3085d6',
      confirmButtonText: 'Aceptar'
    });
  }

  showUsuarioConfirm(result: Result): void {
    const nombre = (result && result.data && result.data.nombreCompleto) || 'Usuario';
    const rol = (result && result.data && result.data.catRolNombre) || 'N/A';
    fireAlert({
      title: 'Usuario Confirmado',
      html: `
        <div style="text-align: left; font-size: 1rem;">
          <p style="margin-bottom: 8px;"><strong>Nombre:</strong> ${nombre}</p>
          <p style="margin-bottom: 0;"><strong>Rol:</strong> ${rol}</p>
        </div>
      `,
      type: 'success',
      confirmButtonColor: '#3085d6',
      confirmButtonText: 'Aceptar'
    });
  }
}
